import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { AddressBookService as AddressBookShipmentService } from "../../services/shipment-service/addressbook";
import { AddressBookService as AddressBookRegistryService } from "../../services/registry-service/addressbook";
import { LocationsService as LocationsShipmentService } from "../../services/shipment-service/locations";
import { LocationsService as LocationsRegistryService } from "../../services/registry-service/locations";
import { ParcelsService } from "../../services/shipment-service/parcels";
import { ShipmentsService } from "../../services/shipment-service/shipments";
import { useOidcUser } from "@axa-fr/react-oidc";
import { needsCustomData, customDataItemCheck } from "./util";
import { CustomDataService } from "../../services/shipment/customData";
import { callErrorToast } from "../../utilities";
import { useAppContext } from "../../AppProvider";

const addressBookShipmentService = new AddressBookShipmentService();
const addressBookRegistryService = new AddressBookRegistryService();
const locationsShipmentService = new LocationsShipmentService();
const locationsRegistryService = new LocationsRegistryService();
const parcelsService = new ParcelsService();
const shipmentsService = new ShipmentsService();
const customDataService = new CustomDataService();

const Save = ({
  data,
  pickup,
  delivery,
  generalInformations,
  parcels,
  editShp,
  checkErrors,
  customData,
}) => {
  const { oidcUser, oidcUserLoadingState } = useOidcUser();
  const navigate = useNavigate();
  const [isClone, setIsClone] = useState(
    useLocation().pathname.includes("clone")
  );
  const [isReturn, setIsReturn] = useState(
    useLocation().pathname.includes("return")
  );

  const [shipment, setShipment] = useState(data);

  const { dictionary } = useAppContext();

  //Create location in registry-service
  const createPickupLocationInAddressBook = () => {
    locationsRegistryService
      .create(pickup.location)
      .then((response) => {
        const newPickup = { ...pickup };
        newPickup.locationId = response.id;
        newPickup.ownerId = oidcUser.owner;
        createPickupAddressBookInAddressBook(newPickup);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Create addressBook in registry-service
  const createPickupAddressBookInAddressBook = (pickup) => {
    pickup.myAddress = true;
    addressBookRegistryService
      .create(pickup)
      .then()
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Edit location in registry-service
  const ediPickupLocationInAddressBook = () => {
    locationsRegistryService
      .edit(pickup.location)
      .then((response) => {
        editPickupAddressBookInAddressBook(pickup);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Edit addressBook in registry-service
  const editPickupAddressBookInAddressBook = (pickup) => {
    pickup.locationId = pickup.location.id;
    addressBookRegistryService
      .edit(pickup)
      .then()
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Create location in registry-service
  const createDeliveryLocationInAddressBook = () => {
    locationsRegistryService
      .create(delivery.location)
      .then((response) => {
        const newDelivery = { ...delivery };
        newDelivery.locationId = response.id;
        newDelivery.ownerId = oidcUser.owner;
        createDeliveryAddressBookInAddressBook(newDelivery);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Create addressBook in registry-service
  const createDeliveryAddressBookInAddressBook = (delivery) => {
    addressBookRegistryService
      .create(delivery)
      .then()
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Edit location in registry-service
  const editDeliveryLocationInAddressBook = () => {
    locationsRegistryService
      .edit(delivery.location)
      .then((response) => {
        editDeliveryAddressBookInAddressBook(delivery);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //Edit addressBook in registry-service
  const editDeliveryAddressBookInAddressBook = (delivery) => {
    delivery.locationId = delivery.location.id;
    addressBookRegistryService
      .edit(delivery)
      .then()
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  //1) Delete pickup addressBook (it also cancels the location) (shipment-service)
  const deletePickupAddressBook = () => {
    if (shipment.pickupId) {
      addressBookShipmentService
        .delete(shipment.pickupId)
        .then()
        .catch((err) => {
          callErrorToast(err, dictionary);
        });
    }
  };

  //2) Create pickup location
  const createPickupLocation = (shipment) => {
    locationsShipmentService
      .create(pickup.location)
      .then((response) => {
        const newPickup = { ...pickup };
        newPickup.locationId = response.id;
        newPickup.ownerId = newPickup.ownerId || oidcUser.owner;
        createPickupAddressBook(shipment, newPickup);
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //3) Create pickup addressBook (shipment-service)
  const createPickupAddressBook = (shipment, newPickup) => {
    addressBookShipmentService
      .create(newPickup)
      .then((response) => {
        shipment.pickupId = response.id;
        //Delete old Pickup addressBook
        deletePickupAddressBook();
        createDeliveryLocation({ ...shipment });
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //4) Delete delivery addressBook (it also cancels the location) (shipment-service)
  const deleteDeliveryAddressBook = () => {
    if (shipment.deliveryId) {
      addressBookShipmentService
        .delete(shipment.deliveryId)
        .then()
        .catch((err) => {
          callErrorToast(err, dictionary);
        });
    }
  };

  //5) Create delivery location
  const createDeliveryLocation = (shipment) => {
    locationsShipmentService
      .create(delivery.location)
      .then((response) => {
        const newDelivery = { ...delivery };
        newDelivery.locationId = response.id;
        newDelivery.ownerId = newDelivery.ownerId || oidcUser.owner;
        createDeliveryAddressBook(shipment, newDelivery);
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //6) Create delivery addressBook (shipment-service)
  const createDeliveryAddressBook = (shipment, newDelivery) => {
    addressBookShipmentService
      .create(newDelivery)
      .then((response) => {
        shipment.deliveryId = response.id;
        //Delete old Delivery addressBook
        deleteDeliveryAddressBook();
        saveShipment(shipment);
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //6) Save shipment (shipment-service)
  const saveShipment = (shipment) => {
    const newShipment = shipmentNormalizer(shipment);
    if (newShipment.id) {
      if (isClone || isReturn) {
        delete shipment.id;
        createShipment(newShipment);
        return false;
      }
      editShipment(newShipment);
      return false;
    }
    delete shipment.id;
    createShipment(newShipment);
    //create
  };

  //6a) Create shipment (shipment-service)
  const createShipment = (shipment) => {
    shipmentsService
      .create(shipment)
      .then((response) => {
        const newShipment = { ...response };
        createParcels(newShipment);
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //6b) Edit shipment (shipment-service)
  const editShipment = (shipment) => {
    shipmentsService
      .edit(shipment)
      .then((response) => {
        deleteParcels(shipment);
      })
      .catch((err) => {
        editShp({ ...shipment });
        callErrorToast(err, dictionary);
      });
  };

  //7) Delete parcels
  const deleteParcels = (shipment) => {
    shipmentsService
      .deleteParcels(shipment.id)
      .then((response) => {
        createParcels(shipment);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  // 8) Create parcels (shipment-service)
  const createParcels = (shipment) => {
    const parcelsNormalized = parcelsNormalizer(shipment);
    parcelsNormalized.forEach((elem, index) => {
      parcelsService
        .create(elem)
        .then((response) => {
          if (index === parcels.length - 1) {
            deleteOptions(shipment);
          }
        })
        .catch((err) => {
          callErrorToast(err, dictionary);
        });
    });
  };

  const deleteOptions = (shipment) => {
    shipmentsService
      .deleteOptions(shipment.id)
      .then((response) => {
        if (shipment.cashOnDeliveryValue || shipment.insuranceValue) {
          createOptions(shipment);
          return false;
        } else {
          removeCustomData(shipment);
          return false;
        }
        //navigate(`/shipments/rate/${shipment.id}`);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const createOptions = (shipment) => {
    let options = [];
    if (shipment.cashOnDeliveryValue) {
      options.push({ surchargeCode: "COD" });
    }
    if (shipment.insuranceValue) {
      options.push({ surchargeCode: "INSURANCE" });
    }
    shipmentsService
      .createOptions(options, shipment.id)
      .then((response) => {
        removeCustomData(shipment);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const removeCustomData = (shipment) => {
    customDataService
      .deleteCustomData(shipment.id)
      .then((response) => {
        if (
          needsCustomData({
            pickup,
            delivery,
            parcelType: generalInformations.parcelType,
          })
        ) {
          saveCustomData(shipment);
          return false;
        }
        navigate(`/shipments/rate/${shipment.id}`);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const saveCustomData = (shipment) => {
    let promises = [];
    customData.map((elem) => {
      elem = customDataNormalizer(elem);

      elem.shipmentId = shipment.id;
      elem.uuid = null;
      return promises.push(customDataService.createCustomData(elem));
    });

    Promise.all(promises)
      .then((response) => {
        navigate(`/shipments/rate/${shipment.id}`);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const customDataNormalizer = (customData) => {
    let customDataNormalized = { ...customData };
    if (customData.unitPriceCurrency) {
      customDataNormalized.unitPriceCurrencyCode =
        customData.unitPriceCurrency.value;
    }
    if (customData.countryIsoCode) {
      customDataNormalized.countryIsoCode2 = customData.countryIsoCode.code;
    }
    if (customData.category) {
      customDataNormalized.categoryCode = customData.category.value;
    }
    if (customData.unitWeight) {
      customDataNormalized.unitWeight = parseFloat(customData.unitWeight);
    }
    if (customData.unitPrice) {
      customDataNormalized.unitPrice = parseFloat(customData.unitPrice);
    }
    if (customData.quantity) {
      customDataNormalized.quantity = parseInt(customData.quantity);
    }
    return customDataNormalized;
  };

  const shipmentNormalizer = (shipment) => {
    let shipmentNormalized = {};
    if (shipment.pickupId) {
      shipmentNormalized.pickupId = shipment.pickupId;
    }
    if (shipment.pickupNote) {
      shipmentNormalized.pickupNote = shipment.pickupNote;
    }
    if (shipment.deliveryId) {
      shipmentNormalized.deliveryId = shipment.deliveryId;
    }
    if (shipment.deliveryNote) {
      shipmentNormalized.deliveryNote = shipment.deliveryNote;
    }
    if (generalInformations.shipmentType) {
      shipmentNormalized.shipmentType = generalInformations.shipmentType.value;
    }
    if (generalInformations.parcelType) {
      shipmentNormalized.parcelTypeCode = generalInformations.parcelType.value;
    }
    if (generalInformations.cashOnDeliveryValue) {
      shipmentNormalized.cashOnDeliveryValue =
        generalInformations.cashOnDeliveryValue;
    }
    if (generalInformations.cashOnDeliveryValueCurrency) {
      shipmentNormalized.cashOnDeliveryValueCurrency =
        generalInformations.cashOnDeliveryValueCurrency.value;
    }
    if (generalInformations.insuranceValue) {
      shipmentNormalized.insuranceValue = generalInformations.insuranceValue;
    }
    if (generalInformations.insuranceValueCurrency) {
      shipmentNormalized.insuranceValueCurrency =
        generalInformations.insuranceValueCurrency.value;
    }
    if (generalInformations.goodsValue) {
      shipmentNormalized.goodsValue = generalInformations.goodsValue;
    }
    if (generalInformations.goodsValueCurrency) {
      shipmentNormalized.goodsValueCurrency =
        generalInformations.goodsValueCurrency.value;
    }
    if (generalInformations.content) {
      shipmentNormalized.content = generalInformations.content;
    }
    if (generalInformations.externalReference) {
      shipmentNormalized.externalReference =
        generalInformations.externalReference;
    }
    if (generalInformations.note) {
      shipmentNormalized.note = generalInformations.note;
    }
    if (shipment.volume) {
      shipmentNormalized.volume = shipment.volume;
    }
    if (shipment.weight) {
      shipmentNormalized.weight = shipment.weight;
    }
    shipmentNormalized.senderId = shipment.senderId || oidcUser.owner;
    shipmentNormalized.id = shipment.id;

    return shipmentNormalized;
  };

  const parcelsNormalizer = (shipment) => {
    let newParcels = [];
    parcels.forEach((elem) => {
      let newParcel = { ...elem };
      newParcel.shipmentId = shipment.id;
      if (elem.templateId) newParcel.parcelTempleateId = elem.templateId.id;
      if (newParcel.quantity > 1) {
        for (let i = 0; i < newParcel.quantity; i++) {
          newParcels.push(newParcel);
        }
        delete newParcel.quantity;
        return true;
      }
      delete newParcel.quantity;
      newParcels.push(newParcel);
    });
    return newParcels;
  };

  const shipmentValidator = () => {
    let errors = [];

    if (!pickup.location.latitude && !pickup.location.longitude) {
      errors.push({
        section: dictionary.registries.sender,
        error: `${dictionary.registries.sender} - ${dictionary.messages.mandatory_field}`,
      });
    }

    if (!pickup.name) {
      errors.push({
        section: dictionary.registries.sender,
        error: `${dictionary.registries.sender} - ${dictionary.messages.mandatory_field}`,
      });
    }

    //Delivery
    if (!delivery.location.latitude && !delivery.location.longitude) {
      errors.push({
        section: dictionary.registries.receiver,
        error: `${dictionary.registries.receiver} - ${dictionary.messages.mandatory_field}`,
      });
    }

    if (!delivery.name) {
      errors.push({
        section: dictionary.registries.receiver,
        error: `${dictionary.registries.receiver} - ${dictionary.messages.mandatory_field}`,
      });
    }

    //Shipment
    if (!generalInformations.parcelType) {
      errors.push({
        section: dictionary.parcels.package_type_2,
        error: `${dictionary.parcels.package_type_2} - ${dictionary.messages.mandatory_field}`,
      });
    }

    //Parcels
    if (parcels.length === 0) {
      errors.push({
        section: dictionary.parcels.packages_2,
        error: "Nella spedizione dev'essere presente almeno un collo",
      });
    } else {
      let parcelsCounter = 0;
      parcels.forEach((elem) => {
        parcelsCounter += parseInt(elem.quantity);
      });
      if (parcelsCounter > 100) {
        errors.push({
          section: dictionary.parcels.packages_2,
          error: `${dictionary.messages.maximum_number_allowed}: 100 ${dictionary.parcels.packages_2}`,
        });
      }

      parcels.every((elem) => {
        if (
          !elem.width ||
          !elem.length ||
          !elem.height ||
          !elem.weight ||
          !elem.quantity
        ) {
          errors.push({
            section: dictionary.parcels.packages_2,
            error: `${dictionary.messages.mandatory_field} - ${dictionary.dimensions.width}, ${dictionary.dimensions.length}, ${dictionary.dimensions.height}, ${dictionary.dimensions.weight} ${dictionary.words.and} ${dictionary.dimensions.quantity}`,
          });
          return false;
        }
      });
    }

    if (
      needsCustomData({
        pickup,
        delivery,
        parcelType: generalInformations.parcelType,
      })
    ) {
      if (customData && customData.length > 0) {
        let customDataItemErrors = [];
        customData.map((item) => {
          let itemErr = customDataItemCheck(item);
          if (itemErr) customDataItemErrors.push(itemErr);
        });
        if (customDataItemErrors && customDataItemErrors.length > 0) {
          errors.push({
            section: dictionary.customs_data.customs_data,
            sectionId: "customData",
            error: `${dictionary.messages.incomplete_data} - ${dictionary.customs_data.customs_data}`,
            innerErrors: customDataItemErrors,
          });
        }
      } else {
        errors.push({
          section: dictionary.customs_data.customs_data,
          sectionId: "customData",
          error: `${dictionary.messages.mandatory_field} - ${dictionary.customs_data.customs_data}`,
        });
      }
    }

    return errors;
  };

  const onSubmitShipment = () => {
    const errors = shipmentValidator(shipment);
    if (errors.length === 0) {
      if (pickup.createInAddressBook) {
        createPickupLocationInAddressBook();
      }
      if (pickup.editInAddressBook) {
        ediPickupLocationInAddressBook();
      }

      if (delivery.createInAddressBook) {
        createDeliveryLocationInAddressBook();
      }
      if (delivery.editInAddressBook) {
        editDeliveryLocationInAddressBook();
      }

      createPickupLocation({ ...shipment });
      return false;
    }
    checkErrors(errors);
    toast.error(
      `${dictionary.words.warning}! ${dictionary.messages.check_fields}.`,
      {
        position: toast.POSITION.TOP_CENTER,
      }
    );
  };

  useEffect(() => {
    setShipment(data);
  }, [data]);

  return (
    <React.Fragment>
      <div className="row">
        <div className="col text-start">
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={() => {
              navigate(`/shipments`);
            }}
          >
            <i className="ph-arrow-left ph-sm me-2"></i>
            {dictionary.words.back}
          </button>
        </div>
        <div className="col text-end">
          <button
            color="light"
            type="button"
            className="btn btn-success"
            onClick={onSubmitShipment}
          >
            {dictionary.rating.choose_rate}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Save;
